import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import Select, { components } from "react-select";
import { format, isValid } from "date-fns";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faTableList,
  faCalendar,
  faHashtag,
  faFile,
  faBookOpen,
  faUser,
  faBuilding,
  faHospital,
  faShieldAlt,
  faUserTie,
  faFilePdf,
  faExternalLinkAlt,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faSyncAlt as faRegenerate,
  faPlus,
  faSpinner,
  faEye,
  faExclamationTriangle,
  faInfoCircle,
  faTimes,
  faUserCircle,
  faIdCard,
  faStethoscope,
  faComment,
  faStar,
  faExpandArrowsAlt,
  faCompressArrowsAlt,
  faEllipsisVertical,
  faEdit,
  faShoppingCart,
  faFilter,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import VIPMemberTooltip_v2 from "./VIPMemberTooltip_v2";
import BatchFileSpecsModal from "../Member/BatchFileSpecsModal";
import TransactionDetailsDialog from "./TransactionDetailsDialog";
import { getDisplayLoaNo } from "./transactionLoaDisplayRules";
const MySwal = withReactContent(Swal);
const LOA_PDF_SPECS_ENDPOINT = `${BASE_URL}/generate-loa/loa-document/specs`;
const LOA_PDF_SPECS_PAGE = "/loa-pdf-generation-specs";
const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  loading,
  itemsPerPage,
  totalCount,
}) => {
  const renderPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const range = [];
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }
    if (currentPage - delta > 2) range.unshift("...");
    if (currentPage + delta < totalPages - 1) range.push("...");
    pages.push(1);
    pages.push(...range);
    if (totalPages > 1) pages.push(totalPages);
    return pages.map((p, i) => (
      <button
        key={i}
        onClick={() => typeof p === "number" && onPageChange(p)}
        disabled={p === "..."}
        className={`p-2 px-3 text-sm font-medium rounded-lg transition-all duration-200 ${
          currentPage === p
            ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25"
            : "text-gray-700 hover:bg-gray-100 border border-gray-200"
        } ${p === "..." ? "opacity-50 cursor-default" : "hover:scale-105"}`}
      >
        {p}
      </button>
    ));
  };
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between p-6 border-t border-gray-100 bg-white/50 backdrop-blur-sm">
      <div className="text-sm text-gray-600 mb-3 md:mb-0">
        Showing{" "}
        <span className="font-semibold text-gray-900">
          {(currentPage - 1) * itemsPerPage + 1}
        </span>{" "}
        to{" "}
        <span className="font-semibold text-gray-900">
          {Math.min(currentPage * itemsPerPage, totalCount)}
        </span>{" "}
        of <span className="font-semibold text-gray-900">{totalCount}</span>{" "}
        results
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1 || loading}
          className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-all duration-200 hover:scale-105"
          aria-label="First page"
        >
          <FontAwesomeIcon icon={faAngleDoubleLeft} className="text-sm" />
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
          className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-all duration-200 hover:scale-105"
          aria-label="Previous page"
        >
          <FontAwesomeIcon icon={faAngleLeft} className="text-sm" />
        </button>
        <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg">
          {renderPageNumbers()}
        </div>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
          className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-all duration-200 hover:scale-105"
          aria-label="Next page"
        >
          <FontAwesomeIcon icon={faAngleRight} className="text-sm" />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || loading}
          className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-all duration-200 hover:scale-105"
          aria-label="Last page"
        >
          <FontAwesomeIcon icon={faAngleDoubleRight} className="text-sm" />
        </button>
      </div>
    </div>
  );
};
// Add this safe filter function at the component level
const safeFilterOption = (option, inputValue) => {
  try {
    const searchText =
      typeof inputValue === "string" ? inputValue : String(inputValue || "");
    if (!searchText.trim()) return true;

    const label = option.label || option.data?.label || "";
    return String(label).toLowerCase().includes(searchText.toLowerCase());
  } catch (error) {
    return true; // Return true if filtering fails
  }
};
// Simple debounce utility
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};
const ACTION_MENU_WIDTH = 288;
const ACTION_MENU_HEIGHT = 340;
const ACTION_MENU_MARGIN = 12;
const ACTION_MENU_GAP = 8;
const TRANSACTION_LIST_STATE_KEY = "allaccess.transactionList.state";
const DEFAULT_TRANSACTION_FILTERS = {
  status: "",
  coverage: "",
  provider: "",
  corporate: "",
  member: "",
  start_date: "",
  end_date: "",
};
const DEFAULT_TRANSACTION_SORT_CONFIG = {
  key: "trans_date",
  direction: "desc",
};

const readTransactionListState = () => {
  if (typeof window === "undefined") return {};

  try {
    const rawState = window.sessionStorage.getItem(TRANSACTION_LIST_STATE_KEY);
    if (!rawState) return {};

    const parsedState = JSON.parse(rawState);
    return parsedState && typeof parsedState === "object" ? parsedState : {};
  } catch (error) {
    return {};
  }
};

const writeTransactionListState = (state) => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(
      TRANSACTION_LIST_STATE_KEY,
      JSON.stringify(state)
    );
  } catch (error) {
    // Ignore storage write failures so browsing the list never breaks.
  }
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - ACTION_MENU_WIDTH;
  left = Math.max(
    ACTION_MENU_MARGIN,
    Math.min(left, viewportWidth - ACTION_MENU_WIDTH - ACTION_MENU_MARGIN)
  );

  let top = rect.bottom + ACTION_MENU_GAP;
  let placement = "bottom";

  if (top + ACTION_MENU_HEIGHT > viewportHeight - ACTION_MENU_MARGIN) {
    top = rect.top - ACTION_MENU_HEIGHT - ACTION_MENU_GAP;
    placement = "top";
  }

  const maxTop = Math.max(
    ACTION_MENU_MARGIN,
    viewportHeight - ACTION_MENU_HEIGHT - ACTION_MENU_MARGIN
  );
  top = Math.max(ACTION_MENU_MARGIN, Math.min(top, maxTop));

  return { top, left, placement };
};
const TransactionList = () => {
  const navigate = useNavigate();
  const initialListState = useMemo(readTransactionListState, []);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(
    typeof initialListState.searchTerm === "string"
      ? initialListState.searchTerm
      : ""
  );
  const [currentPage, setCurrentPage] = useState(
    Number(initialListState.currentPage) > 0
      ? Number(initialListState.currentPage)
      : 1
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    Number(initialListState.itemsPerPage) > 0
      ? Number(initialListState.itemsPerPage)
      : 10
  );
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    ...DEFAULT_TRANSACTION_FILTERS,
    ...(isPlainObject(initialListState.filters) ? initialListState.filters : {}),
  });
  const [sortConfig, setSortConfig] = useState({
    ...DEFAULT_TRANSACTION_SORT_CONFIG,
    ...(isPlainObject(initialListState.sortConfig)
      ? initialListState.sortConfig
      : {}),
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [regeneratingTransNo, setRegeneratingTransNo] = useState(null);
  const [showLoaPdfSpecsModal, setShowLoaPdfSpecsModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // New states for filter options
  const [providerOptions, setProviderOptions] = useState([]);
  const [corporateOptions, setCorporateOptions] = useState([]);
  const [coverageOptions, setCoverageOptions] = useState([]);
  const [trnStatusOptions, setTrnStatusOptions] = useState([]);
  const [filterLoading, setFilterLoading] = useState({
    provider: false,
    corporate: false,
    coverage: false,
    trnStatus: false,
  });
  // Search input states for debounced search
  const [providerSearch, setProviderSearch] = useState("");
  const [corporateSearch, setCorporateSearch] = useState("");
  const [coverageSearch, setCoverageSearch] = useState("");
  const columns = [
    {
      key: "trans_date",
      label: "Transaction Date",
      sortable: true,
      icon: faCalendar,
      width: "w-48",
    },
    {
      key: "loa_date_issued",
      label: "Date Issued",
      sortable: true,
      icon: faCalendar,
      width: "w-48",
    },
    {
      key: "trans_no",
      label: "Transaction #",
      sortable: true,
      icon: faHashtag,
      width: "w-40",
    },
    {
      key: "loa_no",
      label: "LOA #",
      sortable: true,
      icon: faFile,
      width: "w-32",
    },
    {
      key: "full_name",
      label: "Member",
      sortable: true,
      icon: faUser,
      width: "w-64",
    },
    {
      key: "corp_name",
      label: "Corporate",
      sortable: true,
      icon: faBuilding,
      width: "w-64",
    },
    {
      key: "provider_name",
      label: "Provider",
      sortable: true,
      icon: faHospital,
      width: "w-64",
    },
    {
      key: "coverage_code",
      label: "Coverage",
      sortable: true,
      icon: faShieldAlt,
      width: "w-32",
    },
    {
      key: "trn_status",
      label: "TRN Status",
      sortable: true,
      icon: faUserTie,
      width: "w-40",
    },
    {
      key: "issued_by",
      label: "Issued By",
      sortable: true,
      icon: faUserTie,
      width: "w-48",
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      icon: faEllipsisVertical,
      width: "w-32",
    },
  ];
  const actions = [
    {
      key: "edit",
      label: "Edit Generate LOA",
      icon: faEdit,
      color: "text-blue-600",
    },
    {
      key: "view",
      label: "View Transaction Details",
      icon: faEye,
      color: "text-green-600",
    },
    {
      key: "view_loa_v1",
      label: "View LOA from v1",
      icon: faFilePdf,
      color: "text-rose-600",
      requiresUrl: "loa_document_v1_url",
      unavailableLabel: "No v1 LOA document",
    },
    {
      key: "view_loa_v2",
      label: "View LOA from v2",
      icon: faExternalLinkAlt,
      color: "text-indigo-600",
      requiresUrl: "loa_document_v2_url",
      unavailableLabel: "No v2 LOA document",
    },
    {
      key: "regenerate",
      label: "Re-Generate LOA from v2",
      icon: faSyncAlt,
      color: "text-orange-600",
    },
  ];
  const statusMap = {
    Active: {
      color: "bg-green-100 text-green-800 border border-green-200",
      icon: faCheckCircle,
      gradient: "from-green-400 to-emerald-500",
    },
    Pending: {
      color: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      icon: faClock,
      gradient: "from-yellow-400 to-amber-500",
    },
    Cancelled: {
      color: "bg-red-100 text-red-800 border border-red-200",
      icon: faTimesCircle,
      gradient: "from-red-400 to-rose-500",
    },
  };
  // Custom styles for react-select (copied from GenerateLoaTransaction)
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "48px",
      backgroundColor: "white",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      color: "#374151",
      "&:hover": { borderColor: "#3b82f6" },
      fontSize: "14px",
    }),
    input: (provided) => ({
      ...provided,
      color: "#374151",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: "#1f2937",
      fontWeight: "500",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#f3f4f6"
        : "white",
      color: state.isSelected ? "white" : "#374151",
      ":active": {
        ...provided[":active"],
        backgroundColor: state.isSelected ? "#3b82f6" : "#f3f4f6",
        color: state.isSelected ? "white" : "#374151",
      },
      fontSize: "14px",
      padding: "12px 16px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "white",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
      borderRadius: "8px",
      marginTop: "4px",
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: "#6b7280",
      fontSize: "14px",
    }),
    loadingMessage: (provided) => ({
      ...provided,
      color: "#3b82f6",
      fontSize: "14px",
    }),
  };
  const LoadingIndicator = (props) => (
    <components.LoadingIndicator {...props}>
      <FontAwesomeIcon icon={faSpinner} spin className="text-indigo-500 mr-2" />
    </components.LoadingIndicator>
  );
  // Add this normalization function that matches your API response structure
  const normalizeOptions = (options) => {
    if (!options || !Array.isArray(options)) return [];
    return options
      .map((option) => {
        // Handle various possible structures from API
        const value =
          option.id ||
          option.value ||
          option.code ||
          option.status ||
          option.name;
        const label =
          option.text ||
          option.label ||
          option.name ||
          option.description ||
          String(option);
        return {
          value,
          label: String(label),
        };
      })
      .filter((opt) => opt.value && opt.label); // Only valid options
  };
  // Debounced search functions
  const debouncedProviderSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 2) return;
      setFilterLoading((prev) => ({ ...prev, provider: true }));
      try {
        const response = await axios.get(
          `${BASE_URL}/generate-loa/providers/search`,
          {
            params: { q: query },
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        if (response.data.status === "success") {
          setProviderOptions(normalizeOptions(response.data.data));
        }
      } catch (error) {
        toast.error("Failed to search providers.");
      } finally {
        setFilterLoading((prev) => ({ ...prev, provider: false }));
      }
    }, 300),
    []
  );
  const debouncedCorporateSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 2) return;
      setFilterLoading((prev) => ({ ...prev, corporate: true }));
      try {
        const response = await axios.get(
          `${BASE_URL}/generate-loa/corporates/search`,
          {
            params: { q: query },
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        if (response.data.status === "success") {
          setCorporateOptions(normalizeOptions(response.data.data));
        }
      } catch (error) {
        toast.error("Failed to search corporates.");
      } finally {
        setFilterLoading((prev) => ({ ...prev, corporate: false }));
      }
    }, 300),
    []
  );
  const debouncedCoverageSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 2) return;
      setFilterLoading((prev) => ({ ...prev, coverage: true }));
      try {
        const response = await axios.get(
          `${BASE_URL}/generate-loa/coverages/search`,
          {
            params: { q: query },
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        if (response.data.status === "success") {
          setCoverageOptions(normalizeOptions(response.data.data));
        }
      } catch (error) {
        toast.error("Failed to search coverages.");
      } finally {
        setFilterLoading((prev) => ({ ...prev, coverage: false }));
      }
    }, 300),
    []
  );
  // Update your state setters in fetchFilterOptions:
  const fetchFilterOptions = async () => {
    setFilterLoading((prev) => ({
      ...prev,
      provider: true,
      corporate: true,
      coverage: true,
      trnStatus: true,
    }));
    try {
      const [providersRes, corporatesRes, coveragesRes, statusesRes] =
        await Promise.all([
          axios
            .get(`${BASE_URL}/generate-loa/providers/search`, {
              params: { q: "" },
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            })
            .catch(() => ({ data: { status: "error", data: [] } })),
          axios
            .get(`${BASE_URL}/generate-loa/corporates/search`, {
              params: { q: "" },
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            })
            .catch(() => ({ data: { status: "error", data: [] } })),
          axios
            .get(`${BASE_URL}/generate-loa/coverages/search`, {
              params: { q: "" },
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            })
            .catch(() => ({ data: { status: "error", data: [] } })),
          axios
            .get(`${BASE_URL}/transactions/trn-statuses`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            })
            .catch(() => ({ data: { status: "error", data: [] } })),
        ]);
      if (providersRes.data.status === "success") {
        setProviderOptions(normalizeOptions(providersRes.data.data));
      }
      if (corporatesRes.data.status === "success") {
        setCorporateOptions(normalizeOptions(corporatesRes.data.data));
      }
      if (coveragesRes.data.status === "success") {
        setCoverageOptions(normalizeOptions(coveragesRes.data.data));
      }
      if (statusesRes.data.status === "success") {
        setTrnStatusOptions(normalizeOptions(statusesRes.data.data));
      }
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      toast.error("Failed to load filter options.");
    } finally {
      setFilterLoading((prev) => ({
        ...prev,
        provider: false,
        corporate: false,
        coverage: false,
        trnStatus: false,
      }));
    }
  };
  // Effects for debounced searches
  useEffect(() => {
    debouncedProviderSearch(providerSearch);
  }, [providerSearch, debouncedProviderSearch]);
  useEffect(() => {
    debouncedCorporateSearch(corporateSearch);
  }, [corporateSearch, debouncedCorporateSearch]);
  useEffect(() => {
    debouncedCoverageSearch(coverageSearch);
  }, [coverageSearch, debouncedCoverageSearch]);
  const closeActionMenu = useCallback(() => {
    setOpenMenu(null);
    setActionMenuPosition(null);
  }, []);

  const handleActionMenuToggle = useCallback(
    (transaction, event) => {
      event.stopPropagation();

      if (openMenu === transaction.trans_no) {
        closeActionMenu();
        return;
      }

      setActionMenuPosition(getActionMenuPosition(event.currentTarget));
      setOpenMenu(transaction.trans_no);
    },
    [closeActionMenu, openMenu]
  );

  useEffect(() => {
    if (!openMenu) {
      return undefined;
    }

    const handleViewportChange = () => closeActionMenu();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [closeActionMenu, openMenu]);

  const openLoaDocument = (url, missingMessage) => {
    const targetUrl = typeof url === "string" ? url.trim() : "";
    if (!targetUrl) {
      toast.error(missingMessage || "LOA document is not available.");
      return;
    }

    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };
  const handleRegenerateLoaV2 = async (transaction) => {
    const transNo = transaction?.trans_no;
    if (!transNo) {
      toast.error("Transaction number is required for LOA regeneration.");
      return;
    }

    const confirm = await MySwal.fire({
      icon: "question",
      title: "Re-Generate LOA from v2?",
      text: `This will generate a new v2 LOA PDF for Transaction #${transNo}.`,
      showCancelButton: true,
      confirmButtonText: "Re-Generate",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setRegeneratingTransNo(transNo);
    const encodedTransNo = encodeURIComponent(String(transNo));
    try {
      const response = await axios.post(
        `${BASE_URL}/generate-loa/transactions/${encodedTransNo}/loa-document`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      if (response.data.status === "success") {
        toast.success("LOA v2 regenerated successfully.");
        await fetchTransactions();
      } else {
        toast.error(response.data.message || "Failed to regenerate LOA v2.");
      }
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      if (error.response?.status === 403)
        toast.error("You do not have permission to access this.");
      if (error.response?.status === 422)
        toast.error(
          error.response?.data?.message || "Unable to regenerate LOA v2."
        );
      if (error.response?.status === 429)
        toast.error("Rate limit exceeded. Please try again later.");
      if (![401, 403, 422, 429].includes(error.response?.status)) {
        toast.error("Failed to regenerate LOA v2.");
      }
    } finally {
      setRegeneratingTransNo(null);
    }
  };
  const handleAction = async (action, transaction) => {
    if (action === "view_loa_v1") {
      openLoaDocument(
        transaction.loa_document_v1_url,
        "V1 LOA document is not available."
      );
      return;
    }

    if (action === "view_loa_v2") {
      openLoaDocument(
        transaction.loa_document_v2_url,
        "V2 LOA public share token is not available."
      );
      return;
    }

    if (action === "regenerate") {
      await handleRegenerateLoaV2(transaction);
      return;
    }

    if (action === "edit") {
      if (!transaction?.trans_no) {
        toast.error("Transaction number is required for editing.");
        return;
      }

      navigate(
        `/generate-loa-transaction?edit_trans_no=${encodeURIComponent(
          String(transaction.trans_no)
        )}`,
        {
          state: { editTransaction: transaction },
        }
      );
      return;
    }

    if (action === "view") {
      setSelectedTransaction(transaction);
      setShowModal(true);
      setModalLoading(true);
      setModalData(null);
      try {
        const response = await axios.get(
          `${BASE_URL}/transactions/${transaction.trans_no}/details`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        if (response.data.status === "success") {
          setModalData(response.data.data);
        } else {
          toast.error(response.data.message || "Failed to load details.");
          setShowModal(false);
        }
      } catch (error) {
        if (error.response?.status === 401) window.location.href = "/login";
        if (error.response?.status === 403)
          toast.error("You do not have permission to access this.");
        if (error.response?.status === 429)
          toast.error("Rate limit exceeded. Please try again later.");
        toast.error("Failed to fetch transaction details.");
        setShowModal(false);
      } finally {
        setModalLoading(false);
      }
      return;
    }
    MySwal.fire({
      icon: "info",
      title: `${action.toUpperCase()} Action`,
      text: `Placeholder for ${action} on Transaction #${transaction.trans_no}. Coming soon!`,
      confirmButtonText: "OK",
    });
  };
  useEffect(() => {
    fetchFilterOptions(); // Load initial options only once
  }, []); // Empty dependency array for mount only
  useEffect(() => {
    writeTransactionListState({
      searchTerm,
      currentPage,
      itemsPerPage,
      filters,
      sortConfig,
    });
  }, [searchTerm, currentPage, itemsPerPage, filters, sortConfig]);
  useEffect(() => {
    fetchTransactions();
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const limitOffset = (currentPage - 1) * itemsPerPage;
      let params = {
        limit_offset: limitOffset,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };
      if (searchTerm.trim()) params.search_string = searchTerm.trim();
      if (filters.status) params.status = filters.status;
      if (filters.coverage) params.coverage = filters.coverage;
      if (filters.provider) params.provider = filters.provider;
      if (filters.corporate) params.corporate = filters.corporate;
      if (filters.member) params.member = filters.member;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const response = await axios.get(`${BASE_URL}/transactions`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      setTransactions(response.data.data || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      if (error.response?.status === 403)
        toast.error("You do not have permission to access this.");
      if (error.response?.status === 429)
        toast.error("Rate limit exceeded. Please try again later.");
      toast.error("Failed to fetch transactions.");
    } finally {
      setLoading(false);
    }
  };
  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const handleGenerateLoa = () => {
    navigate("/generate-loa-transaction");
  };
  const clearFilters = () => {
    setFilters(DEFAULT_TRANSACTION_FILTERS);
    setSearchTerm("");
    setCurrentPage(1);
    // Reset search inputs
    setProviderSearch("");
    setCorporateSearch("");
    setCoverageSearch("");
  };
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return isValid(date) ? format(date, "yyyy-MM-dd HH:mm") : "-";
  };
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && showModal) {
        setShowModal(false);
        setSelectedTransaction(null);
        setModalData(null);
      }
    };
    if (showModal) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
  }, [showModal]);
  const EmptyState = ({
    title = "No transactions found",
    subtitle = "Try adjusting your search or filters.",
  }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gradient-to-br from-gray-50 to-blue-50/30 p-12 text-center">
      <div className="mb-4 rounded-full bg-indigo-100 p-4">
        <FontAwesomeIcon
          icon={faTableList}
          className="h-8 w-8 text-indigo-600"
        />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500 mb-6 max-w-sm">{subtitle}</p>
      <button
        onClick={handleGenerateLoa}
        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
      >
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        Generate LOA Transaction
      </button>
    </div>
  );
  const LoadingSkeleton = ({ rows, columns }) => (
    <tbody className="bg-white divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="hover:bg-gray-50 animate-pulse">
          {Array.from({ length: columns }).map((_, j) => (
            <td key={j} className="px-4 py-4">
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
  const TransactionDetailsModal = () => {
    const [activeTab, setActiveTab] = useState("transaction");
    const [isMaximized, setIsMaximized] = useState(false);
    if (!showModal || !selectedTransaction) return null;
    const data = modalData || {}; // Fallback to selected for quick view if fetch fails
    const formatCurrency = (amount) =>
      amount
        ? new Intl.NumberFormat("en-PH", {
            style: "currency",
            currency: "PHP",
          }).format(amount)
        : "-";
    const getStatusBadge = (status) => {
      const badges = {
        CANCELLED: {
          class: "bg-red-100 text-red-800",
          icon: faExclamationTriangle,
        },
        "RETURNED BY APPROVER": {
          class: "bg-yellow-100 text-yellow-800",
          icon: faClock,
        },
        "FOR APPROVAL": {
          class: "bg-blue-100 text-blue-800",
          icon: faInfoCircle,
        },
        default: { class: "bg-gray-100 text-gray-800", icon: null },
      };
      const badge = badges[status] || badges.default;
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.class}`}
        >
          {badge.icon && <FontAwesomeIcon icon={badge.icon} className="mr-1" />}
          {status}
        </span>
      );
    };
    const handleTabClick = (id) => {
      setActiveTab(id);
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    const toggleMaximized = () => {
      setIsMaximized((prev) => !prev);
    };
    return (
      <div
        className="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setShowModal(false)}
          />
          {/* Modal Panel */}
          <div
            className={`relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl ${
              isMaximized ? "sm:max-w-full sm:my-2 h-[95vh]" : "sm:my-8"
            }`}
          >
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <FontAwesomeIcon
                        icon={faEye}
                        className="mr-2 text-indigo-600"
                      />
                      Transaction Details:{" "}
                      {data.trans_no || selectedTransaction.trans_no}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-500"
                        onClick={toggleMaximized}
                      >
                        <FontAwesomeIcon
                          icon={
                            isMaximized
                              ? faCompressArrowsAlt
                              : faExpandArrowsAlt
                          }
                          className="text-xl"
                        />
                      </button>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-500"
                        onClick={() => setShowModal(false)}
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xl" />
                      </button>
                    </div>
                  </div>
                  {modalLoading ? (
                    <div className="flex justify-center py-8">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="text-indigo-600 text-2xl animate-spin"
                      />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Tabs for Sections */}
                      <div className="border-b border-gray-200">
                        <nav
                          className="-mb-px flex space-x-8"
                          aria-label="Tabs"
                        >
                          {[
                            {
                              id: "transaction",
                              label: "Transaction Info",
                              icon: faFile,
                            },
                            {
                              id: "member",
                              label: "Member Details",
                              icon: faUserCircle,
                            },
                            {
                              id: "loa",
                              label: "LOA & Coverage",
                              icon: faIdCard,
                            },
                            {
                              id: "diagnosis",
                              label: "Diagnosis & ICD",
                              icon: faStethoscope,
                            },
                            {
                              id: "approval",
                              label: "Approval & Remarks",
                              icon: faComment,
                            },
                          ].map((tab) => (
                            <button
                              key={tab.id}
                              className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                  ? "border-indigo-500 text-indigo-600"
                                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                              }`}
                              onClick={() => handleTabClick(tab.id)}
                            >
                              <FontAwesomeIcon
                                icon={tab.icon}
                                className={`mr-2 h-4 w-4 ${
                                  activeTab === tab.id
                                    ? "text-indigo-500"
                                    : "text-gray-400"
                                }`}
                              />
                              {tab.label}
                            </button>
                          ))}
                        </nav>
                      </div>
                      {/* Tab Content: Use conditional rendering or state for tabs; here showing all in scrollable for simplicity */}
                      <div
                        className={`overflow-y-auto space-y-4 p-2 ${
                          isMaximized ? "max-h-[85vh]" : "max-h-[60vh]"
                        }`}
                      >
                        {/* Transaction Basics */}
                        <div
                          id="transaction"
                          className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        >
                          <div className="bg-gray-50 p-3 rounded-md">
                            <h4 className="font-medium text-gray-900 mb-2">
                              Transaction Date
                            </h4>
                            <p className="text-sm text-gray-700">
                              {formatDate(data.trans_date)}
                            </p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md">
                            <h4 className="font-medium text-gray-900 mb-2">
                              Status
                            </h4>
                            <p>
                              {getStatusBadge(data.loa_no)}{" "}
                              {/* loa_no{getStatusBadge(data.loa_no)}{" "}
                              {/* loa_no is status in SP */}
                            </p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md">
                            <h4 className="font-medium text-gray-900 mb-2">
                              Generated By
                            </h4>
                            <p className="text-sm text-gray-700">
                              {data.generated_by || "-"}
                            </p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md">
                            <h4 className="font-medium text-gray-900 mb-2">
                              LOA Amount
                            </h4>
                            <p className="text-sm font-medium text-gray-900">
                              {formatCurrency(data.loa_amount)}
                            </p>
                          </div>
                        </div>
                        {/* Member Info */}
                        <div id="member">
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                            <FontAwesomeIcon
                              icon={faUserCircle}
                              className="mr-2 text-indigo-600"
                            />
                            Member Information
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-blue-50 p-3 rounded-md">
                              <p className="text-sm text-gray-600">
                                ID: {data.member_id || "-"}
                              </p>
                              <p className="text-sm font-medium text-gray-900">
                                {data.member_name || "-"}
                              </p>
                            </div>
                            <div className="bg-blue-50 p-3 rounded-md">
                              <p className="text-sm text-gray-600">
                                DOB: {formatDate(data.date_of_birth)}
                              </p>
                              <p className="text-sm text-gray-600">
                                Gender: {data.gender || "-"}
                              </p>
                              <p className="text-sm text-gray-600">
                                Civil Status: {data.civil_status || "-"}
                              </p>
                            </div>
                            <div className="bg-blue-50 p-3 rounded-md">
                              <p className="text-sm text-gray-600">
                                Expiry: {formatDate(data.expiry_date)}
                              </p>
                              <p className="text-sm text-gray-600">
                                Special Instructions:{" "}
                                {data.special_instructions ? "Yes" : "No"}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* LOA & Coverage */}
                        <div id="loa">
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                            <FontAwesomeIcon
                              icon={faIdCard}
                              className="mr-2 text-indigo-600"
                            />
                            LOA & Coverage Details
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-green-50 p-3 rounded-md">
                              <p className="text-sm text-gray-600">
                                LOA #: {getDisplayLoaNo(data) || "-"}
                              </p>
                              <p className="text-sm text-gray-600">
                                Coverage Code: {data.coverage_code || "-"}
                              </p>
                              <p className="text-sm text-gray-600">
                                Plan: {data.plan_name || "-"}{" "}
                                {data.shared_dependent
                                  ? "(Shared Dependent)"
                                  : ""}
                              </p>
                              <p className="text-sm text-gray-600">
                                Corporate: {data.corp_name || "-"} (
                                {data.corp_code})
                              </p>
                            </div>
                            <div className="bg-green-50 p-3 rounded-md">
                              <p className="text-sm text-gray-600">
                                Provider: {data.provider_name || "-"} (
                                {data.provider_code})
                              </p>
                              <p className="text-sm text-gray-600">
                                Payment Terms: {data.payment_terms || "-"}
                              </p>
                              <p className="text-sm text-gray-600">
                                Form #: {data.form_number || "-"}
                              </p>
                              <p className="text-sm text-gray-600">
                                Procedure Date: {formatDate(data.proc_date)}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* Diagnosis & ICD */}
                        <div id="diagnosis">
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                            <FontAwesomeIcon
                              icon={faStethoscope}
                              className="mr-2 text-indigo-600"
                            />
                            Diagnosis & ICD
                          </h4>
                          <div className="bg-yellow-50 p-3 rounded-md">
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Diagnosis:</strong>{" "}
                              {data.diagnosis || "-"}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Chief Complaint:</strong>{" "}
                              {data.chief_complaint || "-"}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Requested Procedure:</strong>{" "}
                              {data.requested_lab_procedure || "-"}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Doctor:</strong> {data.doctor_name || "-"}
                            </p>
                            {data.icd_10_cm && (
                              <details className="mt-2">
                                <summary className="text-sm font-medium text-indigo-600 cursor-pointer">
                                  ICD Details (Click to Expand)
                                </summary>
                                <div className="mt-2 text-sm text-gray-700 pl-4">
                                  <p>ICD ID: {data.icd_id || "-"}</p>
                                  <p>ICD 10 CM: {data.icd_10_cm}</p>
                                  <p>Condition: {data.condition || "-"}</p>
                                </div>
                              </details>
                            )}
                          </div>
                        </div>
                        {/* Approval & Remarks */}
                        <div id="approval">
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                            <FontAwesomeIcon
                              icon={faComment}
                              className="mr-2 text-indigo-600"
                            />
                            Approval & Remarks
                          </h4>
                          <div className="bg-purple-50 p-3 rounded-md">
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>LOA Issued:</strong>{" "}
                              {formatDate(data.loa_date_issued)}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>ER Fee:</strong>{" "}
                              {formatCurrency(data.er_fee)}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Prof Fee:</strong>{" "}
                              {formatCurrency(data.prof_fee)}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Physician:</strong>{" "}
                              {data.physician_name || "-"}
                            </p>
                            <details className="mt-2">
                              <summary className="text-sm font-medium text-indigo-600 cursor-pointer">
                                Remarks (Click to Expand)
                              </summary>
                              <div className="mt-2 text-sm text-gray-700 pl-4">
                                <p>{data.remarks || "-"}</p>
                                {data.cancelled_details && (
                                  <p className="text-red-600 mt-1">
                                    Cancelled: {data.cancelled_details}
                                  </p>
                                )}
                                {data.approver_remarks && (
                                  <p className="text-yellow-600 mt-1">
                                    Approver: {data.approver_remarks}
                                  </p>
                                )}
                              </div>
                            </details>
                            {data.loa_document && (
                              <div className="mt-2">
                                <a
                                  href={`${BASE_URL}/storage/loa-documents/${data.loa_document}`} // Assume storage link
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-indigo-600 hover:text-indigo-500 text-sm underline"
                                >
                                  View LOA Document (PDF)
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  // onInputChange handlers - synchronous
  const handleProviderInputChange = (inputValue) => {
    setProviderSearch(inputValue || "");
    return inputValue;
  };
  const handleCorporateInputChange = (inputValue) => {
    setCorporateSearch(inputValue || "");
    return inputValue;
  };
  const handleCoverageInputChange = (inputValue) => {
    setCoverageSearch(inputValue || "");
    return inputValue;
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2">
      <div className="max-w-1xl mx-auto">
        {/* Header - More compact and modern */}
        <div className="px-4 py-4 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-md">
                <FontAwesomeIcon
                  icon={faTableList}
                  className="text-white text-lg"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Transaction List
                </h1>
                <p className="text-gray-500 text-xs mt-0.5">
                  Manage and monitor all LOA transactions
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => setShowLoaPdfSpecsModal(true)}
                className="relative inline-flex items-center justify-center px-4 py-2.5 bg-white text-indigo-700 font-medium rounded-lg border border-indigo-200 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 transition-all duration-200 shadow-md hover:shadow-lg text-sm"
              >
                <span className="absolute -right-2 -top-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-white shadow">
                  New
                </span>
                <FontAwesomeIcon icon={faBookOpen} className="mr-2 text-xs" />
                LOA PDF Generation Specs
              </button>
              <button
                onClick={handleGenerateLoa}
                className="inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 transition-all duration-200 shadow-md hover:shadow-lg text-sm"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2 text-xs" />
                Generate LOA Transaction
              </button>
            </div>
          </div>
        </div>

        {/* Controls - Compact design */}
        <div className="bg-white rounded-lg shadow-xs border border-gray-100 p-4">
          {/* Main Search */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"
              />
              <input
                type="text"
                placeholder="Search transactions, members, LOA numbers..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-9 pr-3 py-2.5 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2.5 bg-white border border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 text-sm shadow-xs"
              >
                <FontAwesomeIcon icon={faFilter} className="mr-1.5 text-xs" />
                Filters
              </button>
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-3 py-2.5 bg-white border border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 text-sm shadow-xs"
              >
                Clear
              </button>
              <button
                onClick={fetchTransactions}
                disabled={loading}
                className="inline-flex items-center px-3 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-medium rounded-lg hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 shadow-sm disabled:opacity-50 text-sm"
              >
                <FontAwesomeIcon
                  icon={faSyncAlt}
                  className={`mr-1.5 text-xs ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>

          {/* Expandable Filters - Ultra compact grid */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3 p-3 bg-gray-50/80 rounded-lg border border-gray-100 mt-3">
              {/* TRN Status */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 block">
                  TRN Status
                </label>
                <Select
                  placeholder="Select status..."
                  value={
                    trnStatusOptions.find(
                      (opt) => opt.value === filters.status
                    ) || null
                  }
                  onChange={(selected) =>
                    handleFilterChange("status", selected ? selected.value : "")
                  }
                  options={trnStatusOptions}
                  isLoading={filterLoading.trnStatus}
                  styles={customStyles}
                  components={{ LoadingIndicator }}
                  isClearable
                  isSearchable={false}
                  filterOption={safeFilterOption}
                  className="text-xs"
                />
              </div>

              {/* Coverage */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 block">
                  Coverage
                </label>
                <Select
                  placeholder="Select coverage..."
                  value={
                    coverageOptions.find(
                      (opt) => opt.value === filters.coverage
                    ) || null
                  }
                  inputValue={coverageSearch}
                  onInputChange={handleCoverageInputChange}
                  onChange={(selected) =>
                    handleFilterChange(
                      "coverage",
                      selected ? selected.value : ""
                    )
                  }
                  options={coverageOptions}
                  isLoading={filterLoading.coverage}
                  styles={customStyles}
                  components={{ LoadingIndicator }}
                  isClearable
                  filterOption={safeFilterOption}
                  className="text-xs"
                />
              </div>

              {/* Provider */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 block">
                  Provider
                </label>
                <Select
                  placeholder="Select provider..."
                  value={
                    providerOptions.find(
                      (opt) => opt.value === filters.provider
                    ) || null
                  }
                  inputValue={providerSearch}
                  onInputChange={handleProviderInputChange}
                  onChange={(selected) =>
                    handleFilterChange(
                      "provider",
                      selected ? selected.value : ""
                    )
                  }
                  options={providerOptions}
                  isLoading={filterLoading.provider}
                  styles={customStyles}
                  components={{ LoadingIndicator }}
                  isClearable
                  filterOption={safeFilterOption}
                  className="text-xs"
                />
              </div>

              {/* Corporate */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 block">
                  Corporate
                </label>
                <Select
                  placeholder="Select corporate..."
                  value={
                    corporateOptions.find(
                      (opt) => opt.value === filters.corporate
                    ) || null
                  }
                  inputValue={corporateSearch}
                  onInputChange={handleCorporateInputChange}
                  onChange={(selected) =>
                    handleFilterChange(
                      "corporate",
                      selected ? selected.value : ""
                    )
                  }
                  options={corporateOptions}
                  isLoading={filterLoading.corporate}
                  styles={customStyles}
                  components={{ LoadingIndicator }}
                  isClearable
                  filterOption={safeFilterOption}
                  className="text-xs"
                />
              </div>

              {/* Date Range - More compact */}
              <div className="space-y-1 md:col-span-2">
                <label className="text-xs font-medium text-gray-700 block">
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) =>
                      handleFilterChange("start_date", e.target.value)
                    }
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent transition-all bg-white text-xs"
                  />
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) =>
                      handleFilterChange("end_date", e.target.value)
                    }
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent transition-all bg-white text-xs"
                  />
                </div>
              </div>

              {/* Items per page */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 block">
                  Items per page
                </label>
                <select
                  value={itemsPerPage}
                  onChange={(e) =>
                    handleItemsPerPageChange(Number(e.target.value))
                  }
                  className="w-full px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent transition-all bg-white text-gray-900 text-xs"
                >
                  {[5, 10, 20, 50, 100].map((val) => (
                    <option key={val} value={val}>
                      Show {val}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-2">
          <div className="overflow-x-auto relative">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider transition-colors ${column.width}`}
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={column.icon}
                          className="mr-2 text-gray-400"
                        />
                        <span>{column.label}</span>
                        {column.sortable && (
                          <button
                            onClick={() => handleSort(column.key)}
                            className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            <FontAwesomeIcon
                              icon={
                                sortConfig.key === column.key
                                  ? sortConfig.direction === "asc"
                                    ? faSortUp
                                    : faSortDown
                                  : faSort
                              }
                              className={`text-sm ${
                                sortConfig.key === column.key
                                  ? "text-indigo-600"
                                  : "text-gray-400"
                              }`}
                            />
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              {loading ? (
                <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
              ) : (
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-12">
                        <EmptyState />
                      </td>
                    </tr>
                  ) : (
                    transactions.map((transaction, idx) => {
                      const displayLoaNo = getDisplayLoaNo(transaction);

                      return (
                        <tr
                          key={idx}
                          className="group hover:bg-gradient-to-r hover:from-indigo-50/30 hover:to-purple-50/30 transition-all duration-200 cursor-pointer"
                        >
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">
                          {formatDate(transaction.trans_date)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {formatDate(transaction.loa_date_issued)}
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-l">
                          {transaction.trans_no || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-indigo-600">
                          {displayLoaNo || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">
                              {transaction.full_name || "-"}
                            </span>
                            {transaction.vip_tag === 1 && (
                              <VIPMemberTooltip_v2 />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {transaction.corp_name || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {transaction.provider_name || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {transaction.coverage_code || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                              statusMap[transaction.trn_status]?.color ||
                              "bg-gray-100 text-gray-800 border border-gray-200"
                            }`}
                          >
                            <FontAwesomeIcon
                              icon={
                                statusMap[transaction.trn_status]?.icon ||
                                faInfoCircle
                              }
                              className="mr-1.5 text-xs"
                            />
                            {transaction.trn_status || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {transaction.issued_by || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium">
                          <div className="relative">
                            <button
                              type="button"
                              aria-label={`Actions for transaction ${transaction.trans_no}`}
                              onClick={(event) =>
                                handleActionMenuToggle(transaction, event)
                              }
                              className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                                openMenu === transaction.trans_no
                                  ? "border-blue-400 bg-blue-50 text-blue-700 shadow-blue-100"
                                  : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={faEllipsisVertical}
                                className="text-gray-500"
                              />
                              <span>Actions</span>
                            </button>
                            {openMenu === transaction.trans_no &&
                              actionMenuPosition &&
                              typeof document !== "undefined" &&
                              createPortal(
                                <>
                                  <button
                                    type="button"
                                    aria-label="Close transaction actions menu"
                                    className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                                    onClick={closeActionMenu}
                                  />
                                  <div
                                    className="fixed z-[9999] w-72 overflow-visible"
                                    style={{
                                      top: `${actionMenuPosition.top}px`,
                                      left: `${actionMenuPosition.left}px`,
                                    }}
                                  >
                                    <div
                                      className={`absolute right-6 h-3 w-3 rotate-45 border bg-white ${
                                        actionMenuPosition.placement === "top"
                                          ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                                          : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                                      }`}
                                    />
                                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                                      <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Transaction Actions
                                        </p>
                                      </div>
                                      <div className="space-y-1 p-2">
                                        {actions.map((action) => {
                                          const isRegenerating =
                                            action.key === "regenerate" &&
                                            regeneratingTransNo ===
                                              transaction.trans_no;
                                          const isDisabled =
                                            isRegenerating ||
                                            (action.requiresUrl &&
                                              !transaction[action.requiresUrl]);
                                          const actionIcon = isRegenerating
                                            ? faSpinner
                                            : action.icon;
                                          const actionLabel = isRegenerating
                                            ? "Regenerating LOA..."
                                            : action.label;

                                          return (
                                            <button
                                              key={action.key}
                                              type="button"
                                              disabled={isDisabled}
                                              onClick={() => {
                                                if (isDisabled) {
                                                  toast.error(
                                                    action.unavailableLabel ||
                                                      "Action is not available."
                                                  );
                                                  return;
                                                }
                                                handleAction(
                                                  action.key,
                                                  transaction
                                                );
                                                closeActionMenu();
                                              }}
                                              className={`group flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-all duration-150 ${
                                                isDisabled
                                                  ? "cursor-not-allowed border-transparent text-gray-400"
                                                  : "border-transparent text-gray-700 hover:translate-x-0.5 hover:border-blue-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 hover:shadow-sm"
                                              }`}
                                            >
                                              <span className="flex min-w-0 items-center">
                                                <span
                                                  className={`mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-150 ${
                                                    isDisabled
                                                      ? "bg-gray-100"
                                                      : "bg-slate-50 group-hover:bg-white group-hover:shadow-sm"
                                                  }`}
                                                >
                                                  <FontAwesomeIcon
                                                    icon={actionIcon}
                                                    className={`text-sm ${
                                                      isDisabled
                                                        ? "text-gray-300"
                                                        : action.color
                                                    } ${
                                                      isRegenerating
                                                        ? "animate-spin"
                                                        : ""
                                                    }`}
                                                  />
                                                </span>
                                                <span className="truncate font-medium">
                                                  {actionLabel}
                                                </span>
                                              </span>
                                              {!isDisabled &&
                                                action.requiresUrl && (
                                                  <FontAwesomeIcon
                                                    icon={faExternalLinkAlt}
                                                    className="ml-3 text-xs text-gray-400 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-blue-500"
                                                  />
                                                )}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                </>,
                                document.body
                              )}
                          </div>
                        </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              )}
            </table>
          </div>
          {/* Pagination */}
          {transactions.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              loading={loading}
              itemsPerPage={itemsPerPage}
              totalCount={totalCount}
            />
          )}
        </div>
      </div>
      {/* Transaction Details Modal */}
      <TransactionDetailsDialog
        isOpen={showModal}
        selectedTransaction={selectedTransaction}
        data={modalData}
        loading={modalLoading}
        onClose={() => {
          setShowModal(false);
          setSelectedTransaction(null);
          setModalData(null);
        }}
      />
      <BatchFileSpecsModal
        endpoint={LOA_PDF_SPECS_ENDPOINT}
        isOpen={showLoaPdfSpecsModal}
        onClose={() => setShowLoaPdfSpecsModal(false)}
        title="LOA PDF Generation Specs"
        fullPagePath={LOA_PDF_SPECS_PAGE}
        fullPageLabel="View full page"
      />
      {/* Tooltip */}
      <Tooltip id="tooltip" />
    </div>
  );
};
export default TransactionList;
